import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import * as VisuallyHiddenPrimitive from "@radix-ui/react-visually-hidden"
import { X } from "lucide-react"

import { cn } from "../../lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, style, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Важно: overlay НЕ должен перекрывать глобальный UI (header + TabBar),
      // иначе тапы по TabBar начинают закрывать текущую панель (ощущается как "назад").
      "fixed left-0 right-0 z-[95] bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    style={{
      top: 'calc(56px + env(safe-area-inset-top, 0px))',
      bottom: 'var(--app-tab-bar-height)',
      ...style,
    }}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, style, ...props }, ref) => {
  // Check if DialogTitle and DialogDescription exist in children
  // Use a more robust check that works in both dev and production
  const childrenArray = React.Children.toArray(children);
  
  const findTitle = (children) => {
    return React.Children.toArray(children).some(child => {
      if (React.isValidElement(child)) {
        const childType = child.type;
        // Check if it's a DialogPrimitive.Title directly
        if (childType === DialogPrimitive.Title) {
          return true;
        }
        // Check by displayName and name (for our wrapped components)
        if (childType?.displayName === 'DialogTitle' ||
            childType?.name === 'DialogTitle') {
          return true;
        }
        // Check if it's a DialogHeader (div with displayName 'DialogHeader')
        // that might contain DialogTitle
        if (childType?.displayName === 'DialogHeader' || 
            childType?.name === 'DialogHeader') {
          return findTitle(child.props.children);
        }
        // Recursively check children
        if (child.props?.children) {
          return findTitle(child.props.children);
        }
      }
      return false;
    });
  };

  const findDescription = (children) => {
    return React.Children.toArray(children).some(child => {
      if (React.isValidElement(child)) {
        const childType = child.type;
        // Check if it's a DialogPrimitive.Description directly
        if (childType === DialogPrimitive.Description) {
          return true;
        }
        // Check by displayName and name (for our wrapped components)
        if (childType?.displayName === 'DialogDescription' ||
            childType?.name === 'DialogDescription') {
          return true;
        }
        // Check if it's a DialogHeader that might contain DialogDescription
        if (childType?.displayName === 'DialogHeader' || 
            childType?.name === 'DialogHeader') {
          return findDescription(child.props.children);
        }
        // Recursively check children
        if (child.props?.children) {
          return findDescription(child.props.children);
        }
      }
      return false;
    });
  };

  const hasTitle = findTitle(childrenArray);
  const hasDescription = findDescription(childrenArray);

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // Panel вместо модалки:
          // - mobile: bottom sheet
          // - sm+: right side sheet
          "fixed z-[95] grid gap-4 border bg-background shadow-lg duration-200",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          // mobile bottom sheet
          "inset-x-0 w-full rounded-t-2xl p-4",
          "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
          "overflow-auto",
          // desktop side sheet
          "sm:inset-y-0 sm:right-0 sm:left-auto sm:h-full sm:w-[420px] sm:max-w-[90vw] sm:rounded-none sm:rounded-l-2xl sm:p-6",
          "sm:data-[state=closed]:slide-out-to-right sm:data-[state=open]:slide-in-from-right",
          className
        )}
        style={{
          // Не перекрывать TabBar: панель всегда "сидит" над таббаром + safe-area
          bottom: 'var(--app-tab-bar-height)',
          // Не уходить под header + safe-area + таббар
          maxHeight:
            'calc(100dvh - 56px - env(safe-area-inset-top, 0px) - var(--app-tab-bar-height))',
          ...style,
        }}
        {...props}>
        {/* Always add hidden title FIRST so Radix UI can find it during its validation check */}
        {!hasTitle && (
          <VisuallyHiddenPrimitive.Root>
            <DialogPrimitive.Title>
              Dialog
            </DialogPrimitive.Title>
          </VisuallyHiddenPrimitive.Root>
        )}
        {children}
        {!hasDescription && (
          <VisuallyHiddenPrimitive.Root>
            <DialogPrimitive.Description>
              Dialog content
            </DialogPrimitive.Description>
          </VisuallyHiddenPrimitive.Root>
        )}
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

// VisuallyHidden component for accessibility (wrapper around Radix UI)
const VisuallyHidden = React.forwardRef(({ className, ...props }, ref) => (
  <VisuallyHiddenPrimitive.Root
    ref={ref}
    className={className}
    {...props} />
))
VisuallyHidden.displayName = "VisuallyHidden"

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  VisuallyHidden,
}
